import { useState, useEffect, useCallback } from 'react';
import { fetchJson } from '../components/services/league-standings-services';
import { FPLTeamInfo } from '../types';

export type DefensivePlayerStat = {
  id: number;
  name: string;
  elementType: 1 | 2 | 3 | 4;
  teamId: number;
  teamShortName: string;
  minutes: number;
  // GK-specific
  saves: number;
  penaltiesSaved: number;
  // Official FPL defensive_contribution stat from fixture stats
  defensiveScore: number;
};

export type LiveFixtureDefensive = {
  fixtureId: number;
  homeTeam: FPLTeamInfo;
  awayTeam: FPLTeamInfo;
  homeScore: number | null;
  awayScore: number | null;
  started: boolean;
  finished: boolean;
  finishedProvisional: boolean;
  kickoffTime: string;
  goalkeepers: DefensivePlayerStat[];
  outfieldPlayers: DefensivePlayerStat[];
};


export const useLiveDefensiveContributions = () => {
  const [fixtures, setFixtures] = useState<LiveFixtureDefensive[]>([]);
  const [currentEvent, setCurrentEvent] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const { id: gw } = await fetchJson<{ id: number }>('/api/current-gameweek');
      setCurrentEvent(gw);

      const [fixturesData, liveData, playersData, teamsData] = await Promise.all([
        fetchJson<any[]>(`/api/${gw}/fixtures`),
        fetchJson<{ elements: any[] }>(`/api/event/${gw}/livePlayerPoints`),
        fetchJson<any[]>('/api/players'),
        fetchJson<FPLTeamInfo[]>('/api/teams'),
      ]);

      const liveMap = new Map<number, any>(liveData.elements.map((el: any) => [el.id, el.stats]));
      const teamMap = new Map<number, FPLTeamInfo>(teamsData.map((t: FPLTeamInfo) => [t.id, t]));

      const playersByTeam = new Map<number, any[]>();
      for (const player of playersData) {
        const list = playersByTeam.get(player.team) ?? [];
        list.push(player);
        playersByTeam.set(player.team, list);
      }

      const result: LiveFixtureDefensive[] = fixturesData.map((fixture: any) => {
        const homeTeam: FPLTeamInfo = teamMap.get(fixture.team_h) ?? {
          id: fixture.team_h,
          name: `Team ${fixture.team_h}`,
          short_name: `T${fixture.team_h}`,
        };
        const awayTeam: FPLTeamInfo = teamMap.get(fixture.team_a) ?? {
          id: fixture.team_a,
          name: `Team ${fixture.team_a}`,
          short_name: `T${fixture.team_a}`,
        };

        // Build defensive_contribution map from fixture stats (same format as bps/bonus)
        const defConMap = new Map<number, number>();
        const defConStat = fixture.stats?.find((s: any) => s.identifier === 'defensive_contribution');
        if (defConStat) {
          [...(defConStat.h ?? []), ...(defConStat.a ?? [])].forEach(
            ({ element, value }: { element: number; value: number }) => defConMap.set(element, value)
          );
        }

        const allStats: DefensivePlayerStat[] = [];

        for (const teamId of [fixture.team_h, fixture.team_a]) {
          const players = playersByTeam.get(teamId) ?? [];
          const team = teamMap.get(teamId);

          for (const player of players) {
            const stats = liveMap.get(player.id);
            if (!stats) continue;
            if (!fixture.started && stats.minutes === 0) continue;

            allStats.push({
              id: player.id,
              name: player.web_name,
              elementType: player.element_type as 1 | 2 | 3 | 4,
              teamId: player.team,
              teamShortName: team?.short_name ?? '',
              minutes: stats.minutes ?? 0,
              saves: stats.saves ?? 0,
              penaltiesSaved: stats.penalties_saved ?? 0,
              defensiveScore: defConMap.get(player.id) ?? 0,
            });
          }
        }

        const goalkeepers = allStats
          .filter(p => p.elementType === 1 && p.minutes > 0)
          .sort((a, b) => b.saves - a.saves || b.minutes - a.minutes);

        const outfieldPlayers = allStats
          .filter(p => p.elementType !== 1 && p.minutes > 0 && p.defensiveScore > 0)
          .sort((a, b) => b.defensiveScore - a.defensiveScore || b.minutes - a.minutes);

        return {
          fixtureId: fixture.id,
          homeTeam,
          awayTeam,
          homeScore: fixture.team_h_score,
          awayScore: fixture.team_a_score,
          started: fixture.started,
          finished: fixture.finished,
          finishedProvisional: fixture.finished_provisional,
          kickoffTime: fixture.kickoff_time,
          goalkeepers,
          outfieldPlayers,
        };
      });

      result.sort((a, b) => {
        const aLive = a.started && !a.finishedProvisional && !a.finished;
        const bLive = b.started && !b.finishedProvisional && !b.finished;
        if (aLive !== bLive) return aLive ? -1 : 1;
        const aUpcoming = !a.started;
        const bUpcoming = !b.started;
        if (aUpcoming !== bUpcoming) return aUpcoming ? -1 : 1;
        return new Date(a.kickoffTime).getTime() - new Date(b.kickoffTime).getTime();
      });

      setFixtures(result);
    } catch (e: any) {
      console.error(e);
      setError('Failed to load defensive contributions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return { fixtures, currentEvent, loading, error, refresh: load };
};
